// Day inspector — the day-header summary: coach note, one row per cell
// (click selects it), four macro tiles, and a whole-day totals correction
// (the old EDIT DAY TOTALS / ADD ADHOC path) plus the macros note to the coach.

import { useState } from 'react';
import { formatApiError } from '../../lib/api';
import { mondayOf } from '../../lib/cardio-queries';
import { isoToday, useDayData } from '../../lib/day-data';
import { C, FONT_DISPLAY, FONT_MONO } from '../../lib/design';
import { FOOD_SLOTS, dateLabel, fmt1, type LedgerRow } from '../../lib/ledger';
import { useLedger } from '../../lib/ledger-state';
import { useLedgerWeek, type LedgerDay } from '../../lib/ledger-week';
import { SurfaceComments } from '../day/SurfaceComments';
import { InspHead } from './InspHead';
import { Bar, Btn, ErrorStrip, Field, Lbl, Note, Sub } from './ui';

const SLOT_TITLE: Record<(typeof FOOD_SLOTS)[number], string> = { lunch: 'Lunch', snack: 'Snack', dinner: 'Dinner' };
const MACRO_FIELDS = [['KCAL', 'cal'], ['PRO', 'p'], ['FAT', 'f'], ['CARB', 'c']] as const;
type Totals = LedgerDay['totals'];

type RowView = {
  row: LedgerRow;
  label: string;
  value: string;
  /** `filled` dim · `gap` warn · `due` signal (today, not yet) · `future` faint */
  tone: 'filled' | 'gap' | 'due' | 'future';
};

function rowsFor(d: LedgerDay): RowView[] {
  const emptyTone = d.isFuture ? 'future' : d.isToday ? 'due' : 'gap';
  const emptyText = { gap: 'MISSING →', due: '+ LOG →', future: '—', filled: '' } as const;
  const rows: RowView[] = [];
  rows.push(
    d.weight != null
      ? { row: 'weight', label: 'Weight', value: `${fmt1(d.weight)} kg`, tone: 'filled' }
      : { row: 'weight', label: 'Weight', value: emptyText[emptyTone], tone: emptyTone },
  );
  for (const slot of FOOD_SLOTS) {
    const m = d.meals[slot];
    const tone = m.log ? 'filled' : m.state === 'missing' ? 'gap' : m.state === 'due' ? 'due' : 'future';
    rows.push({
      row: slot,
      label: SLOT_TITLE[slot],
      value: m.log ? `${Math.round(m.log.cal)} kcal` : emptyText[tone],
      tone,
    });
  }
  const s = d.session;
  rows.push({
    row: 'session',
    label: 'Session',
    value: s.done ? `${s.label} · done` : s.missing ? `${s.label} · MISSING →` : s.label,
    tone: s.missing ? 'gap' : s.done || s.rest || d.isPast ? 'filled' : d.isToday ? 'due' : 'future',
  });
  return rows;
}

const TONE_COLOR: Record<RowView['tone'], string> = {
  filled: C.dim,
  gap: C.warn,
  due: C.signal,
  future: C.faint,
};

function TotalsEditor({ date, totals, onClose }: { date: string; totals: Totals; onClose: () => void }) {
  const day = useDayData(date);
  const [v, setV] = useState<Totals>(totals);
  const mut = day.mutations.daily;
  return (
    <div style={{ marginTop: 12, border: `1px dashed ${C.line}`, padding: 12 }}>
      <Lbl>EDIT DAY TOTALS</Lbl>
      <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.5, marginTop: 4 }}>
        Overrides the day's summed macros as a correction. Meal logs are left as they are.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 10 }}>
        {MACRO_FIELDS.map(([label, k]) => (
          <Field
            key={k}
            label={label}
            min={0}
            step={k === 'cal' ? 1 : 0.1}
            value={v[k]}
            onChange={(n) => setV({ ...v, [k]: Math.max(0, n) })}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose} disabled={mut.isPending}>CANCEL</Btn>
        <Btn
          primary
          disabled={mut.isPending}
          onClick={() =>
            mut.mutate(
              {
                date,
                calories: Math.round(v.cal),
                protein_g: Math.round(v.p * 10) / 10,
                fat_g: Math.round(v.f * 10) / 10,
                carb_g: Math.round(v.c * 10) / 10,
                correction: true,
              },
              { onSuccess: onClose },
            )
          }
        >
          {mut.isPending ? 'SAVING…' : 'SAVE'}
        </Btn>
      </div>
      {mut.error && <div style={{ marginTop: 8 }}><ErrorStrip message={formatApiError(mut.error)} /></div>}
    </div>
  );
}

export function DayInspector({ date }: { date: string }) {
  const { select } = useLedger();
  const week = useLedgerWeek(mondayOf(date), [date]);
  const d = week.byDate.get(date);
  const [editing, setEditing] = useState(false);
  // `18 SEP` → `18 Sep`
  const title = dateLabel(date).replace(/[A-Z]{3}$/, (m) => m[0] + m.slice(1).toLowerCase());

  if (!d) {
    return (
      <div>
        <InspHead date={date} title={title} />
        <div style={{ padding: 16 }}>
          {week.error ? <ErrorStrip message={formatApiError(week.error)} /> : <Sub>LOADING…</Sub>}
        </div>
      </div>
    );
  }

  const tiles: Array<[string, number, number]> = [
    ['KCAL', d.totals.cal, d.target.goal_cal],
    ['PRO', d.totals.p, d.target.goal_protein_g],
    ['FAT', d.totals.f, d.target.goal_fat_g],
    ['CARB', d.totals.c, d.target.goal_carb_g],
  ];

  return (
    <div>
      <InspHead
        date={date}
        title={title}
        right={
          d.isPast && d.mealsLogged < 3 ? (
            <Sub color={C.warn} className="num">{d.mealsLogged}/3 MEALS</Sub>
          ) : undefined
        }
      />
      <div style={{ padding: 16 }}>
        {d.coachNote && (
          <Note label="◆ COACH" style={{ marginBottom: 14 }}>
            {d.coachNote}
          </Note>
        )}
        {rowsFor(d).map((r) => (
          <button
            key={r.row}
            type="button"
            onClick={() => select(date, r.row)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              background: 'transparent',
              border: 0,
              borderBottom: `1px solid ${C.line}`,
              padding: '10px 0',
              fontFamily: FONT_MONO,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 12, color: C.text }}>{r.label}</span>
            <span className="num" style={{ fontSize: 11, color: TONE_COLOR[r.tone], textAlign: 'right' }}>
              {r.value}
            </span>
          </button>
        ))}

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
          {tiles.map(([l, v, g]) => (
            <div key={l} style={{ minWidth: 0 }}>
              <Lbl>{l}</Lbl>
              <div className="num" style={{ fontFamily: FONT_DISPLAY, fontSize: 22, marginTop: 4 }}>
                {Math.round(v)}
                <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.faint }}> /{g}</span>
              </div>
              <Bar value={v} max={g} color={v > g * 1.05 ? C.warn : C.signal} />
            </div>
          ))}
        </div>

        {editing ? (
          <TotalsEditor date={date} totals={d.totals} onClose={() => setEditing(false)} />
        ) : (
          <div style={{ marginTop: 10 }}>
            <Btn ghost onClick={() => setEditing(true)}>EDIT TOTALS</Btn>
          </div>
        )}

        <div style={{ marginTop: 16, border: `1px dashed ${C.line}`, padding: '4px 12px 10px' }}>
          <SurfaceComments date={date} surface="macros" isToday={date === isoToday()} />
        </div>
      </div>
    </div>
  );
}
